import { useEffect, useRef, useState } from "react";
import {
  DestinationCheck,
  OrderBill,
  OrderForm,
  OrderSixBill,
  SearchFormApiParams,
} from "../types";
import orderFormApi from "../api/orderFormApi";
import { Severity } from "../../../type";
import getDestinationApi from "../api/getDestinationApi";

type OrderFormApi = {
  total: number;
  orders: OrderForm[];
};

const limit = 10;

const defaultDestination: DestinationCheck = {
  destinationCd: "",
  destinationName: "",
  destinationAddress: "",
};

/**
 * 本部用注文商品一覧取得
 * @param showAlert エラー表示
 */
export default function useOrderForm(showAlert: (msg: string, severity?: Severity) => void) {
  const [total, setTotal] = useState(0); // 在庫のレコードの総数
  const [orderList, setOrderList] = useState<OrderForm[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [isDestinationLoading, setIsDestinationLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false); // スクロール最下層に達した時、値を変更して発火させるイベント
  const [init, setInit] = useState(false);
  const [destinationCheckData, setDestinationCheckData] = useState<{
    A: DestinationCheck | undefined;
    B: DestinationCheck | undefined;
    C: DestinationCheck | undefined;
  }>({
    A: undefined,
    B: undefined,
    C: undefined,
  });

  const get = async (
    searchParams: SearchFormApiParams,
    isBill?: boolean,
  // eslint-disable-next-line consistent-return
  ): Promise<OrderBill | void> => {
    isFastLoading.current = true;
    setIsLoading(true);
    console.log("get");
    try {
      // offset, limitは必須なのでparamsに付け足す。
      if (isBill) {
        const res = (await orderFormApi(
          {
            ...searchParams,
            ...{
              offset: 0,
              limit,
            },
          },
          isBill,
        )) as OrderBill;
        return res;
      }
      const res = (await orderFormApi({
        ...searchParams,
        ...{
          offset: 0,
          limit,
        },
      })) as OrderFormApi;
      const oList = res.orders;
      setTotal(res.total);
      setOrderList(structuredClone(oList));
      setOffset(oList.length);
    } catch (err) {
      console.error(err);
      showAlert("注文確認情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsLoading(false);
    }
  };

  const getMore = async (searchParams: SearchFormApiParams) => {
    if (orderList.length >= total || !init) {
      isFastLoading.current = false;
      return;
    }
    console.log("getMore");
    isFastLoading.current = true;
    setIsMoreLoading(true);
    try {
      const res = (await orderFormApi({
        limit,
        offset,
        ...searchParams,
      })) as OrderFormApi;
      setTotal(res.total);
      setOrderList((prev) => [...prev, ...res.orders]);
      setOffset((prev) => prev + res.orders.length);
    } catch (err) {
      console.error(err);
      showAlert("注文確認情報の取得に失敗しました。", "error");
    } finally {
      isFastLoading.current = false;
      setIsMoreLoading(false);
    }
  };

  // 得意先の発送先コードを取得する(住所、発送先名、発送先コードのみ)。最初にデフォルト値を取得するのはgetDestinationApiで行う
  const getDestination = async (clientCd: string) => {
    setIsDestinationLoading(true);
    try {
      const res = await getDestinationApi(clientCd);
      setDestinationCheckData(res.destination);
    } catch (err) {
      console.error(err);
      showAlert("発送先情報の取得に失敗しました。", "error");
    } finally {
      setIsDestinationLoading(false);
    }
  };

  return [
    {
      total,
      orderList,
      isLoading,
      isMoreLoading,
      isFastLoading,
      fireMoreLoadEvent,
      destinationCheckData,
      isDestinationLoading,
    },
    {
      setOrderList,
      get,
      getMore,
      getDestination,
      setFireMoreLoadEvent,
      setInit,
      setTotal,
      setDestinationCheckData,
      setIsDestinationLoading,
    },
  ] as const;
}
